// URL sanitising (#1458). Several app URLs carry personal data or bearer
// capabilities: `?email=` on the employer wizard, `?invite=&name=` on team
// settings, `#token=` after WorkOS, and `/match/:token`. GA must never see them,
// so page locations are rebuilt from an allowlist rather than by stripping
// known-bad keys.

/** Query parameters GA may see: campaign attribution plus harmless UI state. */
export const ALLOWED_QUERY_PARAMS: ReadonlySet<string> = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'utm_source_platform',
  'gclid',
  'gbraid',
  'wbraid',
  'tab',
  'error',
]);

const ERROR_CODES: ReadonlySet<string> = new Set([
  'auth_failed',
  'session_expired',
  'not_provisioned',
  'org_suspended',
  'org_rejected',
  'internal',
  'admin_access_restricted',
]);

const CLICK_KEYS: ReadonlySet<string> = new Set(['gclid', 'gbraid', 'wbraid']);
const CLICK_ID_RE = /^[A-Za-z0-9_-]{1,512}$/;
const CONTROL_CHARS_RE = /[\u0000-\u001f\u007f]/g;
const UNSAFE_VALUE_RE = /@|\ufffd|[a-z][a-z0-9+.-]*:|\/\/|www\./i;
const MAX_DECODE_PASSES = 3;

// URLSearchParams already decodes once. Inspect the complete value, including
// nested escapes, before truncation so an unsafe suffix cannot be hidden.
function safeQueryText(value: string): string | undefined {
  let decoded = value;

  for (let pass = 0; pass <= MAX_DECODE_PASSES; pass += 1) {
    decoded = decoded.replace(CONTROL_CHARS_RE, '').trim();
    if (!decoded || UNSAFE_VALUE_RE.test(decoded)) {
      return undefined;
    }

    if (!decoded.includes('%')) {
      return decoded;
    }
    if (pass === MAX_DECODE_PASSES) {
      return undefined;
    }

    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      return undefined;
    }
  }

  return undefined;
}

/** Shared by page locations, landing attribution and persisted attribution reads. */
export function sanitizeQueryValue(
  key: string,
  value: string | null | undefined,
): string | undefined {
  if (!ALLOWED_QUERY_PARAMS.has(key) || value == null) {
    return undefined;
  }

  const safe = safeQueryText(value);
  if (!safe) {
    return undefined;
  }

  if (CLICK_KEYS.has(key)) {
    // Click ids must already have the expected shape after URL parsing; do not
    // turn a malformed id into another id by removing controls or decoding it.
    const trimmed = value.trim();
    return CLICK_ID_RE.test(trimmed) ? trimmed : undefined;
  }
  if (key === 'error') {
    return ERROR_CODES.has(safe) ? safe : undefined;
  }

  return key.startsWith('utm_') ? safe.slice(0, 150).trim() : safe;
}

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// An opaque token: long, URL-safe, and containing a digit (route words don't).
const TOKEN_SEGMENT = /^(?=.*[0-9])[A-Za-z0-9_.~-]{20,}$/;
// Routes whose next segment is always a capability token.
const TOKEN_ROUTES: ReadonlySet<string> = new Set(['match']);

export const ID_PLACEHOLDER = ':id';
export const TOKEN_PLACEHOLDER = ':token';

function decodeSegment(segment: string): string | undefined {
  let decoded = segment;

  for (let pass = 0; pass < MAX_DECODE_PASSES && decoded.includes('%'); pass += 1) {
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      return undefined;
    }
  }

  // A separator hidden inside a segment must never change the route we inspect.
  if (/[%/\\?#\u0000-\u001f\u007f\ufffd]/.test(decoded)) {
    return undefined;
  }

  return decoded;
}

/** Replaces id and token path segments with placeholders. */
export function sanitizePath(pathname: string): string {
  const segments = pathname.split('/');
  const decodedSegments = segments.map(decodeSegment);

  return segments
    .map((segment, index) => {
      if (!segment) {
        return segment;
      }
      const decoded = decodedSegments[index];
      const previous = index > 0 ? decodedSegments[index - 1]?.toLowerCase() : '';

      if (decoded === undefined || previous === undefined || TOKEN_ROUTES.has(previous)) {
        return TOKEN_PLACEHOLDER;
      }
      if (UUID_SEGMENT.test(decoded)) {
        return ID_PLACEHOLDER;
      }
      if (TOKEN_SEGMENT.test(decoded) || decoded.includes('@')) {
        return TOKEN_PLACEHOLDER;
      }
      return segment;
    })
    .join('/');
}

function sanitizeSearch(search: string, marketingConsent: boolean): string {
  const kept = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (CLICK_KEYS.has(key) && !marketingConsent) {
      return;
    }

    const safe = sanitizeQueryValue(key, value);
    if (safe !== undefined) {
      kept.append(key, safe);
    }
  });
  const query = kept.toString();
  return query ? `?${query}` : '';
}

/** Rebuilds a URL without fragments; click IDs additionally require marketing consent. */
export function sanitizeUrl(href: string, marketingConsent = false): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return '';
  }
  return `${url.origin}${sanitizePath(url.pathname)}${sanitizeSearch(url.search, marketingConsent)}`;
}

/** Reduces a referrer to its origin, or '' when absent, unparseable or ignored. */
export function sanitizeReferrer(referrer: string, ignoredHosts: readonly string[]): string {
  if (!referrer) {
    return '';
  }
  try {
    const url = new URL(referrer);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      return '';
    }
    return ignoredHosts.includes(url.host.toLowerCase()) ? '' : url.origin;
  } catch {
    return '';
  }
}

export interface LocationLike {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
}

/**
 * Whether the current URL is safe for GTM to load on: no fragment, only allowed
 * query parameters and no token path segments. Pages that scrub a token or an
 * email address from the URL leave this false until they have done so.
 */
export function isLocationClean(location: LocationLike): boolean {
  if (location.hash && location.hash !== '#') {
    return false;
  }
  for (const [key, value] of new URLSearchParams(location.search)) {
    if (sanitizeQueryValue(key, value) === undefined) {
      return false;
    }
  }
  return !sanitizePath(location.pathname).split('/').includes(TOKEN_PLACEHOLDER);
}
